import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGhgSupplierSurveySummaryByCountry } from '../hooks';
import { TimeRange } from 'src/views/components/Dashboard/Stats';
import { timeConverter } from 'src/utils/timeConverter';
import { ReportingFreq } from 'src/utils/api.interfaces';

interface Props {
  organizationId: number;
  reportingPeriod: ReportingFreq;
  timeRange: TimeRange | null;
  branches?: string[];
}

// C10 Goal 2 -- "Podsumowanie per kraj" card: one row per country in scope
// + a totals row, straight from C08's bulk endpoint. Same Box/Table
// styling as SupplierSummary (mounted the same way, by SuperDashboard), no
// meters/pills -- plain text values throughout (Guardrail 1).
export default function CountrySummary({
  organizationId,
  reportingPeriod,
  timeRange,
  branches = [],
}: Props) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'dashboard.country-summary-table',
  });
  const start = timeRange
    ? timeConverter(timeRange.start, reportingPeriod)
    : undefined;
  const end = timeRange
    ? timeConverter(timeRange.end, reportingPeriod)
    : undefined;

  const { data } = useGhgSupplierSurveySummaryByCountry(
    organizationId,
    start,
    end,
    branches
  );

  // BR-2/EC-2: an ambient branch scope of zero must render empty, never the
  // caller's whole scope -- axios drops an empty `branches` array from the
  // querystring entirely, so an unguarded request here would come back
  // unfiltered (C08 treats an absent filter as "don't narrow", same as
  // every other endpoint in this feature). Mirrors the guard
  // SupplierSummary/SupplierSurveys apply to their own rows; there is no
  // per-org rescue here (unlike those tables) since a country aggregate
  // has no single organization_id to check a selected-orgs list against.
  const rows = branches.length > 0 ? (data?.rows ?? []) : [];
  const totals = branches.length > 0 ? data?.totals : undefined;

  // Display-only formatting of the two counts C08 already returns for THIS
  // row -- never summed across rows, never re-aggregated (see the Report:
  // suppliers/registered/branch_count/survey_complete/scope12_complete are
  // all untouched server values).
  const formatRegistered = (registered: number, suppliers: number) => {
    if (!suppliers) return `${registered}`;
    return `${registered} (${Math.round((registered / suppliers) * 100)}%)`;
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('country', 'Country')}</TableCell>
            <TableCell>{t('branches', 'Branches')}</TableCell>
            <TableCell>{t('suppliers', 'Suppliers')}</TableCell>
            <TableCell>{t('registered', 'Registered')}</TableCell>
            <TableCell>{t('surveyComplete', 'Survey complete')}</TableCell>
            <TableCell>{t('scope12Complete', 'Scope 1+2 complete')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.country_code ?? '__no_country__'}>
              <TableCell>
                {row.country_code
                  ? row.country_name
                  : t('noCountry', 'No country')}
              </TableCell>
              <TableCell>{row.branch_count}</TableCell>
              <TableCell>{row.suppliers}</TableCell>
              <TableCell>
                {formatRegistered(row.registered, row.suppliers)}
              </TableCell>
              <TableCell>{row.survey_complete}</TableCell>
              <TableCell>{row.scope12_complete}</TableCell>
            </TableRow>
          ))}
          {totals && (
            <TableRow>
              <TableCell>
                <Typography fontWeight={600}>{t('total', 'Total')}</Typography>
              </TableCell>
              <TableCell />
              <TableCell>{totals.suppliers}</TableCell>
              <TableCell>
                {formatRegistered(totals.registered, totals.suppliers)}
              </TableCell>
              <TableCell>{totals.survey_complete}</TableCell>
              <TableCell>{totals.scope12_complete}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
